import React, { useState, useEffect, createContext, useContext, useReducer, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { imageUrl } from '../../../../../Components/helper';
import noImage from '../../../../../assets/images/no-image.png';
import * as poTemplateAction from '../../store/POTemplate.action';

const POTemplateDndContext = createContext(null);

const initialState = {
  sections: [
    {
      id: 1,
      sectionKey: 'shipping_details_section',
      sequence: 1,
      columns: [[]],
    },
    {
      id: 2,
      sectionKey: 'order_details_section',
      sequence: 2,
      columns: [[]],
    },
    {
      id: 3,
      sectionKey: 'grand_total_section',
      sequence: 3,
      columns: [[]],
    },
  ],
};

function dndReducer(state, action) {
  switch (action.type) {
    case 'BATCH_UPDATE_SECTIONS':
      return {
        ...state,
        sections: action.sections,
      };
    default:
      return state;
  }
}

const sectionOperations = {
  addSection: (sections, action) => {
    const newSection = {
      id: action.id || Date.now(),
      columns: Array.from({ length: action.columns }, () => []),
      position: action.position,
      sequence: 0,
      name: action.name || 'custom Section',
    };

    if (action.position === 'start') {
      newSection.sequence = 1;
      const updatedSections = sections.map((section) => ({
        ...section,
        sequence: section.sequence + 1,
      }));
      return [newSection, ...updatedSections];
    }
    if (action.position === 'end') {
      const maxSequence = Math.max(...sections.map((s) => s.sequence), 0);
      newSection.sequence = maxSequence + 1;
      return [...sections, newSection];
    }
    const targetSectionId = parseInt(action.position, 10);
    const targetSection = sections.find((s) => s.id === targetSectionId);

    if (!targetSection) {
      const maxSequence = Math.max(...sections.map((s) => s.sequence), 0);
      newSection.sequence = maxSequence + 1;
      return [...sections, newSection];
    }

    newSection.sequence = targetSection.sequence;
    const updatedSections = sections.map((section) => {
      if (section.sequence >= targetSection.sequence) {
        return { ...section, sequence: section.sequence + 1 };
      }
      return section;
    });

    return [...updatedSections, newSection].sort((a, b) => a.sequence - b.sequence);
  },

  deleteSection: (sections, sectionId) => {
    const sectionToDelete = sections.find((section) => section.id === sectionId);
    if (!sectionToDelete) {
      return sections;
    }

    const remainingSections = sections.filter((section) => section.id !== sectionId);

    return remainingSections.map((section) => {
      if (section.sequence > sectionToDelete.sequence) {
        return { ...section, sequence: section.sequence - 1 };
      }
      return section;
    });
  },

  updateSectionName: (sections, sectionId, name) => {
    return sections.map((section) => (section.id === sectionId ? { ...section, name } : section));
  },

  addControl: (sections, action) => {
    return sections.map((section) =>
      section.id === action.sectionId
        ? {
            ...section,
            columns:
              section.columns?.map((col, idx) => {
                if (idx === action.columnIdx) {
                  const controlToAdd = {
                    ...action.control,
                    id: action.control.id || Date.now() + Math.random() + Math.random(), // Ensure unique ID if not provided
                  };
                  return [...(col || []), controlToAdd];
                }
                return col;
              }) || [],
          }
        : section,
    );
  },

  editControl: (sections, action) => {
    return sections.map((section) =>
      section.id === action.sectionId
        ? {
            ...section,
            columns:
              section.columns?.map((col, idx) =>
                idx === action.columnIdx ? (col || []).map((ctrl, i) => (i === action.controlIdx ? { ...ctrl, ...action.newProps } : ctrl)) : col,
              ) || [],
          }
        : section,
    );
  },

  deleteControl: (sections, action) => {
    return sections.map((section) =>
      section.id === action.sectionId
        ? {
            ...section,
            columns: section.columns?.map((col, idx) => (idx === action.columnIdx ? (col || []).filter((_, i) => i !== action.controlIdx) : col)) || [],
          }
        : section,
    );
  },

  moveControl: (sections, action) => {
    return sections.map((section) =>
      section.id === action.sectionId
        ? {
            ...section,
            columns:
              section.columns?.map((col, idx) => {
                if (idx === action.columnIdx) {
                  const controls = [...(col || [])];
                  const [movedControl] = controls.splice(action.sourceIndex, 1);
                  controls.splice(action.targetIndex, 0, movedControl);
                  return controls;
                }
                return col;
              }) || [],
          }
        : section,
    );
  },

  addControlAtPosition: (sections, action) => {
    return sections.map((section) =>
      section.id === action.sectionId
        ? {
            ...section,
            columns:
              section.columns?.map((col, idx) => {
                if (idx === action.columnIdx) {
                  const controls = [...(col || [])];
                  const controlToAdd = {
                    ...action.control,
                    id: action.control.id || Date.now() + Math.random() + Math.random(), // Ensure unique ID if not provided
                  };
                  controls.splice(action.position, 0, controlToAdd);
                  return controls;
                }
                return col;
              }) || [],
          }
        : section,
    );
  },
};

export function POTemplateDndProvider({ children, previewData = null }) {
  const [state, dispatch] = useReducer(dndReducer, initialState);
  const reduxDispatch = useDispatch();

  const reduxState = useSelector((state) => state);

  const templateDetails = previewData || reduxState.purchaseOrderTemplateReducer?.newPreviewData || reduxState.purchaseOrderTemplateReducer?.templateDetails;

  const { id } = useParams();
  const userInfoLogo = JSON.parse(localStorage.getItem('loggedInUserInfo'))?.companyLogo;
  let companyLogo = noImage;
  if (userInfoLogo?.includes('super-admin')) {
    companyLogo = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userInfoLogo}`;
  } else if (userInfoLogo?.includes('s3.eu-west-2')) {
    companyLogo = userInfoLogo;
  } else if (userInfoLogo?.startsWith('http') && userInfoLogo?.startsWith('https')) {
    const href = new URL(userInfoLogo);
    companyLogo = `${imageUrl}${href.pathname}`;
  } else {
    companyLogo = `${imageUrl}/${userInfoLogo}`;
  }

  const [detailState, setDetailState] = useState({
    poImage: null,
    originalPOImage: null,
    purchaseOrderNumber: '',
    purchaseOrderDate: null,
    showDeleteSectionModal: false,
    deleteSectionId: null,
    deleteSectionName: '',
    isLoading: false,
    deleteS3Images: false,
    validationErrors: {
      templateName: '',
      packType: {},
      customFields: {},
    },
    sections: [
      {
        id: 1,
        sectionKey: 'shipping_details_section',
        sequence: 1,
        columns: [[]],
        name: 'Shipping Details',
      },
      {
        id: 2,
        sectionKey: 'order_details_section',
        sequence: 2,
        columns: [[]],
        name: 'Order Details',
      },
      {
        id: 3,
        sectionKey: 'grand_total_section',
        sequence: 3,
        columns: [[]],
        name: 'Grand Total',
      },
    ],
    shippingDetails: {
      shipmentMethod: '',
      incoterms: '',
      shippingCosts: '',
      portOfLanding: '',
      portOfLoading: '',
      shippingImage: null,
    },
    grandTotal: {
      grandTotalUnits: '',
      grandTotalPriceAfterDiscount: '',
      shippingCosts: '',
    },
    orderDetails: [
      {
        packType: null,
        productNo: null,
        colourWay: null,
        size: null,
        length: null,
        ratio: 0,
        packs: 0,
        quantity: 0,
        location: null,
        unitPrice: 0,
        discount: 0,
        priceAfterDiscount: 0,
        total: 0,
        exFactoryDate: null,
        shipmentDate: null,
      },
    ],
    templateName: '',
  });

  useEffect(() => {
    if (templateDetails) {
      const processedSections =
        templateDetails.sections?.map((section) => ({
          ...section,
          columns: section.columns?.map(
            (column) =>
              column?.map((control) => ({
                ...control,
                value: control.value || control.defaultValue || '',
              })) || [],
          ) || [[]], // Ensure at least one empty column if no columns exist
          name: section.name || `Section ${section.id}`, // Ensure section has a name
        })) || detailState.sections;

      setDetailState((prev) => ({
        ...prev,
        templateName: templateDetails.templateName || '',
        purchaseOrderNumber: templateDetails.purchaseOrderNumber || '',
        purchaseOrderDate: templateDetails.purchaseOrderDate || null,
        poImage: templateDetails.companyLogo || null,
        originalPOImage: templateDetails.originalPOImage || null,
        sections: processedSections,
        shippingDetails: {
          shipmentMethod: templateDetails.shippingDetails?.shippingMethod || '',
          incoterms: templateDetails.shippingDetails?.incoterms || '',
          shippingCosts: templateDetails.shippingDetails?.shippingAndHandlingCosts || '',
          portOfLanding: templateDetails.shippingDetails?.portOfLanding || '',
          portOfLoading: templateDetails.shippingDetails?.portOfLading || '',
          shippingImage: templateDetails.shippingDetails?.shippingImage || null,
        },
        grandTotal: {
          grandTotalUnits: templateDetails.grandTotal?.grandTotalUnits || '',
          grandTotalPriceAfterDiscount: templateDetails.grandTotal?.grandTotalPriceAfterDiscount || '',
          shippingCosts: templateDetails.grandTotal?.shippingCost || '',
        },
        orderDetails: templateDetails.orderDetails || prev.orderDetails,
      }));
    }
  }, [templateDetails]);

  // Memoized custom dispatch for better performance
  const customDispatch = useCallback(
    async (action) => {
      if (action.type === 'RESET_POTEMPLATE_DETAILS') {
        reduxDispatch(poTemplateAction.clearPurchaseOrderTemplateDetails());
        setDetailState((prev) => ({ ...prev, templateDetails: null, deleteS3Images: false, templateName: '' }));
        return;
      }
      const currentSections = detailState.sections || [];
      let newSections = currentSections;
      switch (action.type) {
        case 'ADD_SECTION':
          newSections = sectionOperations.addSection(currentSections, action);
          break;
        case 'DELETE_SECTION':
          newSections = sectionOperations.deleteSection(currentSections, action.sectionId);
          break;
        case 'UPDATE_SECTION_NAME':
          newSections = sectionOperations.updateSectionName(currentSections, action.sectionId, action.name);
          break;
        case 'ADD_CONTROL':
          newSections = sectionOperations.addControl(currentSections, action);
          break;
        case 'EDIT_CONTROL':
          newSections = sectionOperations.editControl(currentSections, action);
          break;
        case 'DELETE_CONTROL':
          newSections = sectionOperations.deleteControl(currentSections, action);
          break;
        case 'MOVE_CONTROL':
          newSections = sectionOperations.moveControl(currentSections, action);
          break;
        case 'ADD_CONTROL_AT_POSITION':
          newSections = sectionOperations.addControlAtPosition(currentSections, action);
          break;
        case 'BATCH_UPDATE_SECTIONS': {
          const updatedSections = action.sections?.map((section, index) => {
            const cleanSection = { ...section };
            delete cleanSection?._id;
            delete cleanSection?.title;

            const preservedColumns = (section.columns || [[]]).map((column) => (Array.isArray(column) ? column.map((control) => ({ ...control })) : column));
            return {
              ...cleanSection,
              sequence: index + 1,
              id: section.id || section._id,
              columns: preservedColumns.length > 0 ? preservedColumns : [[]],
              name: section.name || `Section ${section.id}`,
            };
          });
          setDetailState((prev) => ({
            ...prev,
            sections: updatedSections,
          }));
          return;
        }
        default:
          dispatch(action);
          return;
      }
      if (newSections !== currentSections) {
        setDetailState((prev) => ({
          ...prev,
          sections: newSections,
        }));
      }
    },
    [detailState.sections, reduxDispatch, id],
  );

  const fetchTemplateDetails = async () => {
    setDetailState((prev) => ({ ...prev, isLoading: true }));
    const res = await reduxDispatch(poTemplateAction.getPurchaseOrderTemplateDetails(id, false));
    if (res?.code === 200) {
      setDetailState((prev) => ({ ...prev, templateDetails: res.data, isLoading: false }));
    } else {
      setDetailState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (id) {
      fetchTemplateDetails();
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch: customDispatch,
      detailState,
      setDetailState,
      companyLogo,
      bulkUpdateSections: (sections) => {
        customDispatch({
          type: 'BATCH_UPDATE_SECTIONS',
          sections,
        });
      },
      paramId: id,
      setValidationError: (field, error, index = null) => {
        setDetailState((prev) => ({
          ...prev,
          validationErrors: {
            ...prev.validationErrors,
            [field]:
              index !== null
                ? {
                    ...prev.validationErrors[field],
                    [index]: error,
                  }
                : error,
          },
        }));
      },
      clearValidationError: (field, index = null) => {
        setDetailState((prev) => ({
          ...prev,
          validationErrors: {
            ...prev.validationErrors,
            [field]:
              index !== null
                ? {
                    ...prev.validationErrors[field],
                    [index]: '',
                  }
                : '',
          },
        }));
      },
      clearAllValidationErrors: () => {
        setDetailState((prev) => ({
          ...prev,
          validationErrors: {
            templateName: '',
            packType: {},
            customFields: {},
          },
        }));
      },
    }),
    [state, customDispatch, detailState, companyLogo, id],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <POTemplateDndContext.Provider value={contextValue}>{children}</POTemplateDndContext.Provider>
    </DndProvider>
  );
}

POTemplateDndProvider.propTypes = {
  children: PropTypes.node.isRequired,
  previewData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

POTemplateDndProvider.defaultProps = {
  previewData: null,
};

export const usePOTemplateDnd = () => useContext(POTemplateDndContext);
